import { Nullable } from 'modules/types/common';
import { createReducer } from '@reduxjs/toolkit';
import { switchAccountSuccess } from 'store/account/actions';
import * as actions from './actions';
import { LabHistoricalResult, ShareLabResults } from '../types';

// *************************
// ** Lab HISTORICAL **
// *************************

export interface LabHistoricalState {
  millennium: {
    isFetching: boolean;
    data: LabHistoricalResult[] | null;
    error: Nullable<Error>;
  };
  cdr: {
    isFetching: boolean;
    data: LabHistoricalResult[] | null;
    error: Nullable<Error>;
  };
}

export const labHistoricalInitialState = (): LabHistoricalState => ({
  millennium: {
    isFetching: false,
    data: null,
    error: null
  },
  cdr: {
    isFetching: false,
    data: null,
    error: null
  }
});

export const labHistoricalReducer = createReducer(labHistoricalInitialState(), {
  [actions.GET_LAB_HISTORICAL_MILLENNIUM]: state => {
    state.millennium.isFetching = true;
    state.millennium.error = null;
  },
  [actions.GET_LAB_HISTORICAL_MILLENNIUM_FAIL]: (state, action) => {
    state.millennium.isFetching = false;
    state.millennium.error = action.error;
  },
  [actions.GET_LAB_HISTORICAL_MILLENNIUM_SUCCESS]: (state, action) => {
    state.millennium.isFetching = false;
    state.millennium.data = action.payload.data.results || action.payload.data || [];
  },
  [actions.GET_LAB_HISTORICAL_CDR]: state => {
    state.cdr.isFetching = true;
    state.cdr.error = null;
  },
  [actions.GET_LAB_HISTORICAL_CDR_FAIL]: (state, action) => {
    state.cdr.isFetching = false;
    state.cdr.error = action.error;
  },
  [actions.GET_LAB_HISTORICAL_CDR_SUCCESS]: (state, action) => {
    state.cdr.isFetching = false;
    state.cdr.data = action.payload.data.results || action.payload.data || [];
  },
  [actions.RESET_LAB_HISTORICAL_CDR]: state => {
    state.cdr = labHistoricalInitialState().cdr;
  },
  [switchAccountSuccess.type]: () => labHistoricalInitialState()
});

type LabDetailsDownloadStatus = 'pending' | 'success' | 'fail';

export interface LabDetailsDownloadsState {
  isDownloadingLabDetails: boolean;
  data: Record<string, LabDetailsDownloadStatus>;
}

export const labDetailsDownloadsInitialState = (): LabDetailsDownloadsState => ({
  isDownloadingLabDetails: false,
  data: {}
});

export const labDetailsDownloadsReducer = createReducer(labDetailsDownloadsInitialState(), {
  [actions.DOWNLOAD_LAB_DETAILS]: (state, action) => {
    state.isDownloadingLabDetails = true;
    state.data[action.payload.id] = 'pending';
  },
  [actions.DOWNLOAD_LAB_DETAILS_SUCCESS]: (state, action) => {
    state.isDownloadingLabDetails = false;
    state.data[action.payload.id] = 'success';
  },
  [actions.DOWNLOAD_LAB_DETAILS_FAIL]: (state, action) => {
    state.isDownloadingLabDetails = false;
    state.data[action.payload.id] = 'fail';
  }
});

export interface PatientLetterDownloadsState {
  isDownloadingLetter: boolean;
  data: Record<string, LabDetailsDownloadStatus>;
}

export const patientLetterDownloadInitialState = (): PatientLetterDownloadsState => ({
  isDownloadingLetter: false,
  data: {}
});

export const patientLetterDownloadsReducer = createReducer(patientLetterDownloadInitialState(), {
  [actions.DOWNLOAD_PATIENT_LETTERS]: (state, action) => {
    state.isDownloadingLetter = true;
    state.data[action.payload.id] = 'pending';
  },
  [actions.DOWNLOAD_PATIENT_LETTERS_SUCCESS]: (state, action) => {
    state.isDownloadingLetter = false;
    state.data[action.payload.id] = 'success';
  },
  [actions.DOWNLOAD_PATIENT_LETTERS_FAIL]: (state, action) => {
    state.isDownloadingLetter = false;
    state.data[action.payload.id] = 'fail';
  }
});

export interface ShareLabResultsState {
  isSendingLabResults: boolean;
  sharedLabResultsData: ShareLabResults | {};
  sharedLabResultsError: Nullable<Error>;
}

export const shareLabResultsInitialState = (): ShareLabResultsState => ({
  isSendingLabResults: false,
  sharedLabResultsData: {},
  sharedLabResultsError: null
});

export const shareLabResultsReducer = createReducer(shareLabResultsInitialState(), {
  [actions.SHARE_LAB_RESULTS]: state => {
    state.isSendingLabResults = true;
    state.sharedLabResultsError = null;
  },
  [actions.SHARE_LAB_RESULTS_SUCCESS]: (state, action) => {
    state.isSendingLabResults = false;
    state.sharedLabResultsData = action.payload.data;
  },
  [actions.SHARE_LAB_RESULTS_FAIL]: (state, action) => {
    state.isSendingLabResults = false;
    state.sharedLabResultsError = action.error;
  }
});
